// src/pages/BroadcastedRouteView.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PipelineTrack, RouteDetailHeader, StepBanner } from '../components/routeKit';
import { classifyMerchantRoute } from '../utils/merchantRouteTracking';
import QRCode from 'react-qr-code';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import {
  getMerchantToken,
  apiUrl,
  getRouteCostPreview,
  initiateRouteChapaPayment,
  verifyChapaRoutePayment,
  getRouteSettlement,
} from '../services/api';
import DeliveryFeePanel, { DriverDebtConfirmModal } from '../components/DeliveryFeePanel';
import { hubCoordsFromRouteResult, isRoundTripTour } from '../utils/merchantPickupLocation';
import { useMerchantStore } from '../store/useMerchantStore';
import {
  actualStopCostFromPreview,
  buildCostPreviewDerived,
  formatVehicleLabel,
} from '../utils/routeCostPreview';
import {
  removeRouteDispatchMark,
} from '../utils/merchantRouteTracking';
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";
import { phoneLinkHtml } from "../components/orderKit";

const OSRM_BASE_URL = (process.env.REACT_APP_OSRM_BASE_URL || '/osrm').replace(/\/$/, '');

function merchantChapaTxStorageKey(routeId) {
  return `odos_merchant_chapa_tx_${routeId}`;
}

/** sessionStorage is per browser tab; Chapa opens a new tab and redirects there - use localStorage too so the return tab can poll/verify. */
function persistMerchantPendingChapaTx(routeId, txRef) {
  const v = txRef != null ? String(txRef).trim() : '';
  if (!v) return;
  const key = merchantChapaTxStorageKey(routeId);
  try {
    sessionStorage.setItem(key, v);
    localStorage.setItem(key, v);
  } catch (_) {
    /* ignore */
  }
}

function readMerchantPendingChapaTx(routeId) {
  const key = merchantChapaTxStorageKey(routeId);
  try {
    return (
      sessionStorage.getItem(key) ||
      localStorage.getItem(key) ||
      ''
    ).trim();
  } catch (_) {
    return '';
  }
}

function clearMerchantPendingChapaTx(routeId) {
  const key = merchantChapaTxStorageKey(routeId);
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch (_) {
    /* ignore */
  }
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Chapa often redirects before their verify API reports success. Flutter waits until the user taps "Done"
 * on the checkout WebView; we approximate that with retries + polling verify on the merchant SPA.
 */
async function verifyChapaPaymentWithRetries(routeId, trxRef, verifyFn, opts = {}) {
  const maxAttempts = opts.maxAttempts ?? 24;
  const delayMs = opts.delayMs ?? 1500;
  let lastErr;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await verifyFn(routeId, trxRef);
      return { ok: true };
    } catch (e) {
      lastErr = e;
      if (i < maxAttempts - 1) await delay(delayMs);
    }
  }
  return { ok: false, error: lastErr };
}

/** Chapa return URLs vary; collect likely query keys. */
function extractTrxRefFromSearchParams(params) {
  const keys = [
    'trx_ref',
    'tx_ref',
    'Trx_ref',
    'reference',
    'txRef',
    'trxRef',
  ];
  for (const k of keys) {
    const v = params.get(k)?.trim();
    if (v) return v;
  }
  return '';
}

/** Axios/merge sometimes yields routeId as string; Number.isFinite(rid) must hold or handlers exit silently. */
function parseRouteIdNumeric(resultRouteId, routeIdParam) {
  if (resultRouteId != null && String(resultRouteId).trim() !== '') {
    const n = Number(resultRouteId);
    if (Number.isFinite(n)) return n;
  }
  if (routeIdParam != null && String(routeIdParam).trim() !== '') {
    const n = Number.parseInt(String(routeIdParam), 10);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

function pickFirstNonEmptyString(...candidates) {
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return '';
}

/** Matches backend: COMPLETED or payment_completed_at set (Instant → ISO string in JSON). */
function routeLooksPaid(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const ps = pickFirstNonEmptyString(obj.paymentStatus, obj.payment_status).toUpperCase();
  if (ps === 'COMPLETED') return true;
  const at = obj.paymentCompletedAt ?? obj.payment_completed_at;
  return at != null && String(at).trim() !== '';
}

/** If either payload shows paid, prefer COMPLETED so stale /details cannot hide a completed list row (or vice versa). */
function pickMergedPaymentAndQr(primary, secondary) {
  const paid = routeLooksPaid(primary) || routeLooksPaid(secondary);
  const psA = pickFirstNonEmptyString(primary.paymentStatus, primary.payment_status);
  const psB = pickFirstNonEmptyString(secondary.paymentStatus, secondary.payment_status);
  const atA = primary.paymentCompletedAt ?? primary.payment_completed_at;
  const atB = secondary.paymentCompletedAt ?? secondary.payment_completed_at;

  let paymentStatus = '';
  let paymentCompletedAt = null;
  if (paid) {
    paymentStatus = 'COMPLETED';
    const chosenAt = atA ?? atB ?? null;
    paymentCompletedAt =
      chosenAt != null && String(chosenAt).trim() !== '' ? chosenAt : null;
  } else {
    paymentStatus = pickFirstNonEmptyString(psA, psB);
    const rawAt = atA ?? atB ?? null;
    paymentCompletedAt =
      rawAt != null && String(rawAt).trim() !== '' ? rawAt : null;
  }

  const qrMerged = pickFirstNonEmptyString(
    primary.qrPayload,
    primary.qrCode,
    secondary.qrPayload,
    secondary.qrCode
  );
  return { paymentStatus, paymentCompletedAt, qrPayload: qrMerged };
}

/** Align /details with GET /routes row (payment + QR sometimes missing from one payload). */
async function mergeBroadcastedRouteWithMerchantList(
  routeData,
  routeIdNum,
  merchantId,
  authFetch
) {
  if (!merchantId || !Number.isFinite(routeIdNum)) return routeData;
  try {
    const listRes = await authFetch(`/api/orders/routes?merchantId=${merchantId}`);
    if (!listRes.ok) return routeData;
    const allRoutes = await listRes.json();
    const row = Array.isArray(allRoutes)
      ? allRoutes.find((r) => Number(r.routeId) === routeIdNum)
      : null;
    if (!row) return routeData;

    const pm = pickMergedPaymentAndQr(routeData, row);
    return {
      ...routeData,
      ...(pm.paymentStatus
        ? { paymentStatus: pm.paymentStatus, payment_status: pm.paymentStatus }
        : {}),
      ...(pm.paymentCompletedAt != null
        ? {
            paymentCompletedAt: pm.paymentCompletedAt,
            payment_completed_at: pm.paymentCompletedAt,
          }
        : {}),
      ...(pm.qrPayload ? { qrPayload: pm.qrPayload, qrCode: pm.qrPayload } : {}),
      driverId: routeData.driverId ?? row.driverId,
      driverPhone: routeData.driverPhone ?? row.driverPhone,
      status:
        pickFirstNonEmptyString(routeData.status, row.status) || routeData.status,
    };
  } catch (e) {
    console.warn('mergeBroadcastedRouteWithMerchantList:', e);
    return routeData;
  }
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function BroadcastedRouteView() {
  const { state } = useLocation();
  const { routeId } = useParams();
  const navigate = useNavigate();

  // We keep merchantId from navigation state (passed from list)
  const merchantIdFromState = state?.merchantId;
  const merchantIdFromStore = useMerchantStore((s) => s.merchantId);

  const [result, setResult] = useState(state?.result || {});
  const [selectedOrders, setSelectedOrders] = useState(state?.selectedOrders || []);

  /** After Chapa redirect, `state` may be empty; still merge list rows using logged-in merchant id. */
  const merchantIdForMerge = useMemo(() => {
    const fromOrder = Array.isArray(selectedOrders)
      ? selectedOrders.find((o) => o?.merchantId != null || o?.merchant?.id != null)
      : null;
    const fromOrderId = fromOrder?.merchantId ?? fromOrder?.merchant?.id ?? null;
    return (
      merchantIdFromState ??
      merchantIdFromStore ??
      fromOrderId ??
      state?.merchantId ??
      null
    );
  }, [merchantIdFromState, merchantIdFromStore, selectedOrders, state?.merchantId]);

  /** Expanded pricing breakdown - mirrors odos_client cost fold-out (single source: `/cost-preview`). */
  const [costBreakdownExpanded, setCostBreakdownExpanded] = useState(false);
  const [loading, setLoading] = useState(!state?.result);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [costPreview, setCostPreview] = useState(null);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  /** Open while the merchant confirms the driver's debt deduction before paying: { debtEtb, senderCashEtb }. */
  const [debtConfirm, setDebtConfirm] = useState(null);
  /** OSRM / polyline draws - do not use page `loading` (would block layout / map mount after Chapa return). */
  const [routeGeometryLoading, setRouteGeometryLoading] = useState(false);

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const driverMarkerRef = useRef(null);
  const liveDriverPolylineRef = useRef(null);
  const driverPolylineDebounceRef = useRef(null);
  const resultRef = useRef(result);
  resultRef.current = result;
  const [driverPosition, setDriverPosition] = useState(null);
  /** When the driver last reported (ms epoch) and whether the backend considers it stale. */
  const [driverSeenAt, setDriverSeenAt] = useState(null);
  const driverSeenAtRef = useRef(null);
  driverSeenAtRef.current = driverSeenAt;
  const [driverStale, setDriverStale] = useState(false);
  /** Keep the map on the driver; a manual pan turns it off, the live chip turns it back on. */
  const [followDriver, setFollowDriver] = useState(true);
  const followDriverRef = useRef(true);
  followDriverRef.current = followDriver;
  const [, setNowTick] = useState(0);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = getMerchantToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(apiUrl(path), { ...options, headers });
  }, []);

  // Keep driver + payment + QR in sync with GET /routes list (same DB row as /details).
  useEffect(() => {
    if (!merchantIdForMerge || !result.routeId) return;

    const fetchAssignmentInfo = async () => {
      try {
        const res = await authFetch(`/api/orders/routes?merchantId=${merchantIdForMerge}`);
        if (!res.ok) return;

        const allRoutes = await res.json();
        const rid = Number(result.routeId);
        const matchingRoute = Array.isArray(allRoutes)
          ? allRoutes.find((r) => Number(r.routeId) === rid)
          : null;

        if (!matchingRoute) return;

        setResult((prev) => {
          const pm = pickMergedPaymentAndQr(prev, matchingRoute);
          return {
            ...prev,
            ...(matchingRoute.driverId != null
              ? { driverId: matchingRoute.driverId }
              : {}),
            ...(matchingRoute.driverPhone != null && matchingRoute.driverPhone !== ''
              ? { driverPhone: matchingRoute.driverPhone }
              : {}),
            ...(matchingRoute.status ? { status: matchingRoute.status } : {}),
            ...(pm.paymentStatus
              ? { paymentStatus: pm.paymentStatus, payment_status: pm.paymentStatus }
              : {}),
            ...(pm.paymentCompletedAt != null
              ? {
                  paymentCompletedAt: pm.paymentCompletedAt,
                  payment_completed_at: pm.paymentCompletedAt,
                }
              : {}),
            ...(pm.qrPayload ? { qrPayload: pm.qrPayload, qrCode: pm.qrPayload } : {}),
          };
        });
      } catch (err) {
        console.warn('Could not fetch assignment info:', err);
      }
    };

    fetchAssignmentInfo();
  }, [result.routeId, merchantIdForMerge, authFetch]);

  const effectiveMerchantId = useMemo(() => {
    return (
      merchantIdFromState ||
      merchantIdFromStore ||
      selectedOrders[0]?.merchantId ||
      selectedOrders[0]?.merchant?.id ||
      null
    );
  }, [merchantIdFromState, merchantIdFromStore, selectedOrders]);

  const isAssigned = !!result?.driverId;
  const effectiveStatus = isAssigned ? 'ASSIGNED' : 'BROADCASTED';

  const paymentStatusNormalized = pickFirstNonEmptyString(
    result?.paymentStatus,
    result?.payment_status
  );
  const paymentCompleted = routeLooksPaid(result);
  const routePickedUp = result?.pickedUp === true;
  const routeCompleted = String(result?.status || '').toUpperCase() === 'COMPLETED';
  /** A driver accepted and the route isn't finished: track them to the store, then along the stops. */
  const driverOnRoute = isAssigned && !routeCompleted;
  const routeAlreadyDispatchedOrScanned = routePickedUp;
  const canUnassignAndRebroadcast =
    effectiveStatus === 'ASSIGNED' && !routeAlreadyDispatchedOrScanned;

  useEffect(() => {
    if (!result.routeId) return;
    if (!(effectiveStatus === 'BROADCASTED' || effectiveStatus === 'ASSIGNED')) return;

    let cancelled = false;
    async function loadPreview() {
      setCostPreviewLoading(true);
      setCostPreviewError(null);
      try {
        const data = await getRouteCostPreview(result.routeId);
        if (!cancelled) setCostPreview(data);
      } catch (err) {
        const msg =
          friendlyError(err, t("price.load_failed"));
        if (!cancelled) setCostPreviewError(msg);
      } finally {
        if (!cancelled) setCostPreviewLoading(false);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [result.routeId, effectiveStatus]);

  useEffect(() => {
    if (!effectiveMerchantId || !routeId) return;

    const socket = new SockJS(apiUrl('/ws'));
    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: (() => {
        const token = getMerchantToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      })(),
      reconnectDelay: 4000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    const rid = parseInt(routeId, 10);
    client.onConnect = () => {
      client.subscribe(`/topic/merchant/routes/${effectiveMerchantId}`, (message) => {
        try {
          const dto = JSON.parse(message.body);
          if (
            dto &&
            dto.routeId != null &&
            Number(dto.routeId) === Number(rid)
          ) {
            setResult((prev) => ({ ...prev, ...dto }));
          }
        } catch (_) {
          /* ignore */
        }
      });
    };

    client.activate();
    return () => {
      try {
        client.deactivate();
      } catch (_) {
        /* ignore */
      }
    };
  }, [effectiveMerchantId, routeId]);

  // Live driver GPS stream (same topic used by odos_client).
  useEffect(() => {
    const rid = Number.parseInt(String(routeId), 10);
    if (!Number.isFinite(rid)) return;
    if (!driverOnRoute) return;

    const socket = new SockJS(apiUrl('/ws'));
    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: (() => {
        const token = getMerchantToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      })(),
      reconnectDelay: 4000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      client.subscribe(`/topic/route/${rid}/driver-location`, (message) => {
        try {
          const dto = JSON.parse(message.body || '{}');
          const lat = Number(dto.latitude);
          const lng = Number(dto.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setDriverPosition({ lat, lng });
            setDriverSeenAt(Date.now());
            setDriverStale(false);
          }
        } catch (_) {
          /* ignore malformed messages */
        }
      });
    };

    client.activate();
    return () => {
      try {
        client.deactivate();
      } catch (_) {
        /* ignore */
      }
    };
  }, [routeId, driverOnRoute]);

  // Last known position from the server: shows the driver at once and covers socket drops.
  useEffect(() => {
    const rid = Number.parseInt(String(routeId), 10);
    if (!Number.isFinite(rid) || !driverOnRoute) {
      setDriverPosition(null);
      setDriverSeenAt(null);
      setDriverStale(false);
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await authFetch(`/api/orders/routes/${rid}/driver-tracking`);
        if (!res.ok || cancelled) return;
        const snap = await res.json();
        if (cancelled || !snap?.trackingActive) return;
        const lat = Number(snap.driverLatitude);
        const lng = Number(snap.driverLongitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const at = snap.lastLocationAt ? Date.parse(snap.lastLocationAt) : null;
        setDriverStale(!!snap.locationStale);
        // A socket ping can be newer than the DB row; keep whichever is latest.
        const prev = driverSeenAtRef.current;
        if (prev != null && at != null && at < prev) return;
        setDriverPosition({ lat, lng });
        if (at != null) setDriverSeenAt(at);
      } catch (_) {
        /* next tick or the socket catches up */
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    // Re-render the "updated Xs ago" label between pings.
    const tick = setInterval(() => setNowTick((n) => n + 1), 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearInterval(tick);
    };
  }, [routeId, driverOnRoute, authFetch]);

  const refreshRouteDetails = useCallback(async () => {
    const rid = parseRouteIdNumeric(result.routeId, routeId);
    if (!Number.isFinite(rid)) return;
    const res = await authFetch(`/api/orders/routes/${rid}/details`);
    if (res.ok) {
      const routeData = await res.json();
      const merged = await mergeBroadcastedRouteWithMerchantList(
        routeData,
        rid,
        merchantIdForMerge,
        authFetch
      );
      setResult(merged);
    }
  }, [result.routeId, routeId, authFetch, merchantIdForMerge]);

  // Fallback while waiting for a driver: the live socket can miss the acceptance.
  useEffect(() => {
    if (isAssigned || paymentCompleted) return undefined;
    const timer = setInterval(() => {
      refreshRouteDetails().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [isAssigned, paymentCompleted, refreshRouteDetails]);

  const startPayment = async () => {
    const debt = Number(result?.driverDebtDeductionEtb || 0);
    if (debt <= 0) {
      handlePayChapa();
      return;
    }
    const rid = parseRouteIdNumeric(result.routeId, routeId);
    try {
      const settlement = await getRouteSettlement(rid);
      if (Number(settlement?.driverDebtDeductionEtb || 0) <= 0) {
        handlePayChapa();
        return;
      }
      setDebtConfirm({
        debtEtb: Number(settlement.driverDebtDeductionEtb),
        senderCashEtb: Number(settlement.senderPaysEtb || 0),
      });
    } catch (err) {
      setActionError(friendlyError(err));
    }
  };

  const handlePayChapa = async () => {
    const rid = parseRouteIdNumeric(result.routeId, routeId);
    if (!Number.isFinite(rid)) return;
    setPaymentBusy(true);
    setActionError(null);
    let checkoutTab = null;
    try {
      // Opening the tab synchronously preserves the user gesture; window.open after await is often blocked.
      checkoutTab = window.open('about:blank', '_blank');
      if (checkoutTab) {
        try {
          checkoutTab.opener = null;
        } catch (_) {
          /* ignore */
        }
      }
      const returnUrl = window.location.href.split('#')[0];
      const data = await initiateRouteChapaPayment(rid, returnUrl);
      const tx = data?.txRef != null ? String(data.txRef) : '';
      if (tx) persistMerchantPendingChapaTx(rid, tx);
      const url = data?.checkoutUrl;
      if (!url) throw new Error(t("live.no_checkout_url"));
      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (err) {
      if (checkoutTab && !checkoutTab.closed) {
        try {
          checkoutTab.close();
        } catch (_) {
          /* ignore */
        }
      }
      const msg = friendlyError(err);
      setActionError(msg);
    } finally {
      setPaymentBusy(false);
    }
  };

  // Debug logging when result changes
  useEffect(() => {
    if (Object.keys(result).length === 0) return;

    console.log('[DETAIL VIEW DEBUG] ────────────────────────────────');
    console.log('Route ID:', routeId);
    console.log('DRIVER ID:', result.driverId);
    console.log('HAS DRIVER:', !!result.driverId);
    console.log('STATUS FIELD:', result.status);
    console.log('PAYMENT STATUS:', paymentStatusNormalized || '(missing)');
    console.log('DRIVER PHONE:', result.driverPhone);
    console.log('Full result keys:', Object.keys(result));
    console.log('effectiveStatus used:', effectiveStatus);
    console.log('──────────────────────────────────────────────────────');
  }, [result, routeId, paymentStatusNormalized]);

  // Load route details; if Chapa redirected here with trx_ref/tx_ref, verify first so we don't flash "unpaid".
  useEffect(() => {
    if (!routeId) return;

    let cancelled = false;

    async function fetchRouteDetails() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams(window.location.search);
        const trxRef = extractTrxRefFromSearchParams(params);

        if (trxRef.startsWith('odos-route-')) {
          const ridParsed = Number.parseInt(String(routeId), 10);
          persistMerchantPendingChapaTx(ridParsed, trxRef);
          // Do not await long retries here - Chapa often redirects before verify succeeds (Flutter avoids this by
          // verifying only after the user taps "Done"). Background retries + polling below mirror that delay.
          void verifyChapaPaymentWithRetries(
            ridParsed,
            trxRef,
            verifyChapaRoutePayment,
            { maxAttempts: 24, delayMs: 1500 }
          ).then(({ ok }) => {
            if (cancelled) return;
            if (ok) clearMerchantPendingChapaTx(ridParsed);
            else {
              console.warn(
                'Post-Chapa verify still pending after retries; polling continues until payment completes.'
              );
            }
          });
          if (!cancelled) {
            for (const k of [
              'trx_ref',
              'tx_ref',
              'Trx_ref',
              'reference',
              'txRef',
              'trxRef',
            ]) {
              params.delete(k);
            }
            const qs = params.toString();
            window.history.replaceState(
              {},
              '',
              `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
            );
          }
        }

        const res = await authFetch(`/api/orders/routes/${routeId}/details`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const routeData = await res.json();

        const merchantId =
          merchantIdFromState ||
          merchantIdFromStore ||
          state?.merchantId ||
          selectedOrders[0]?.merchantId ||
          routeData.merchantId ||
          selectedOrders[0]?.merchant?.id;

        let routeOrders = [];
        if (merchantId) {
          const ordersRes = await authFetch(`/api/orders?merchantId=${merchantId}`);
          if (!ordersRes.ok) throw new Error(`HTTP ${ordersRes.status}`);
          const allOrders = await ordersRes.json();

          routeOrders = allOrders.filter((order) =>
            routeData.orderedOrderIds?.includes(order.id)
          );
          if (!cancelled) setSelectedOrders(routeOrders);
        }

        const ridNum = Number.parseInt(String(routeId), 10);
        const mergeId =
          merchantIdFromState ||
          merchantIdFromStore ||
          state?.merchantId ||
          routeOrders[0]?.merchantId ||
          routeOrders[0]?.merchant?.id ||
          merchantId ||
          null;
        const merged = await mergeBroadcastedRouteWithMerchantList(
          routeData,
          ridNum,
          mergeId,
          authFetch
        );

        if (!cancelled) setResult(merged);
      } catch (err) {
        console.error('Failed to fetch route details:', err);
        if (!cancelled) setError(friendlyError(err, t("routes.open_failed")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchRouteDetails();
    return () => {
      cancelled = true;
    };
  }, [routeId, authFetch, merchantIdFromState, merchantIdFromStore, state?.merchantId]);

  // Returning from Chapa (other tab) or after webhook: refresh when tab becomes visible.
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshRouteDetails();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('pageshow', refreshWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('pageshow', refreshWhenVisible);
    };
  }, [refreshRouteDetails]);

  // After opening Chapa, webhook may land slightly after first GET - poll only if we have a pending tx ref for this route.
  useEffect(() => {
    if (!routeId || paymentCompleted) return;
    const rid = Number.parseInt(String(routeId), 10);
    if (!Number.isFinite(rid)) return;
    if (!readMerchantPendingChapaTx(rid)) return;

    let attempts = 0;
    const maxAttempts = 30;
    const timer = setInterval(async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(timer);
        return;
      }
      try {
        const tx = readMerchantPendingChapaTx(rid);
        if (tx) {
          try {
            await verifyChapaRoutePayment(rid, tx);
            clearMerchantPendingChapaTx(rid);
          } catch (_) {
            /* Chapa verify not ready yet - keep polling */
          }
        }
        const res = await authFetch(`/api/orders/routes/${rid}/details`);
        if (!res.ok) return;
        const data = await res.json();
        const merged = await mergeBroadcastedRouteWithMerchantList(
          data,
          rid,
          merchantIdForMerge,
          authFetch
        );
        if (routeLooksPaid(merged)) {
          setResult(merged);
          clearMerchantPendingChapaTx(rid);
          clearInterval(timer);
        }
      } catch (_) {
        /* ignore */
      }
    }, 2000);

    return () => clearInterval(timer);
  }, [routeId, paymentCompleted, authFetch, merchantIdForMerge]);

  const routeStopIdsKey = useMemo(() => {
    if (!Array.isArray(result.orderedOrderIds) || result.orderedOrderIds.length === 0) return '';
    return result.orderedOrderIds.join(',');
  }, [result.orderedOrderIds]);

  const { orderedOrders, allPoints } = useMemo(() => {
    const hub = hubCoordsFromRouteResult(result);
    if (!result.orderedOrderIds?.length) {
      return { orderedOrders: [], allPoints: [hub] };
    }

    const orders = result.orderedOrderIds
      .map((id) => selectedOrders.find((o) => o.id === id))
      .filter(Boolean);

    const points = [
      hub,
      ...orders
        .map((o) => [parseFloat(o?.deliveryLatitude), parseFloat(o?.deliveryLongitude)])
        .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng)),
      ...(isRoundTripTour(result) ? [hub] : []),
    ];

    return { orderedOrders: orders, allPoints: points };
  }, [result, selectedOrders]);

  const orderedStopIds = result.orderedOrderIds ?? [];
  const isSingleStopRoute = orderedStopIds.length <= 1;
  const nextLiveStopPoint = useMemo(() => {
    const firstUndelivered = orderedOrders.find(
      (o) => String(o?.status || '').toUpperCase() !== 'DELIVERED'
    );
    const target = firstUndelivered || orderedOrders[0];
    const lat = Number.parseFloat(target?.deliveryLatitude);
    const lng = Number.parseFloat(target?.deliveryLongitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return null;
  }, [orderedOrders]);

  const [hubLat, hubLng] = hubCoordsFromRouteResult(result);
  const liveTargetPoint = useMemo(
    () => (routePickedUp ? nextLiveStopPoint : [hubLat, hubLng]),
    [routePickedUp, nextLiveStopPoint, hubLat, hubLng]
  );

  const costPreviewDerived = useMemo(
    () => buildCostPreviewDerived(costPreview, result.orderedOrderIds),
    [costPreview, result.orderedOrderIds]
  );

  /** Only after platform fee payment is completed; hide pre-sign placeholder payloads. */
  const rawQr =
    (result.qrPayload && String(result.qrPayload).trim()) ||
    (result.qrCode && String(result.qrCode).trim()) ||
    '';
  const qrParts = rawQr.split('|');
  const qrLooksPlaceholder =
    /^ODOSv1\|pending\|/i.test(rawQr) ||
    (qrParts[0] === 'ODOSv1' &&
      qrParts.length < 5 &&
      String(qrParts[1] || '').toLowerCase() === 'pending');
  /** Backend marks payment complete; show stored qr_payload unless it is still the pre-sign placeholder. */
  const qrPayloadForDisplay =
    paymentCompleted && rawQr && !qrLooksPlaceholder ? rawQr : '';

  // Map init after route stops exist (Chapa return has no React state → first paint had no map ref; [] deps never re-ran).
  useEffect(() => {
    if (!routeId || !routeStopIdsKey) return undefined;

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;

    const cancelRaf = () => {
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const mount = () => {
      if (cancelled || leafletMap.current) return;
      if (!mapRef.current) {
        attempts += 1;
        if (attempts < 48) rafId = requestAnimationFrame(mount);
        return;
      }

      const hub = hubCoordsFromRouteResult(resultRef.current);
      leafletMap.current = L.map(mapRef.current).setView(hub, 13);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(leafletMap.current);

      cancelRaf();
      rafId = requestAnimationFrame(() => {
        if (cancelled || !leafletMap.current) return;
        leafletMap.current.invalidateSize();
        setMapReady(true);
      });
    };

    rafId = requestAnimationFrame(mount);

    return () => {
      cancelled = true;
      cancelRaf();
      setMapReady(false);
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
  }, [routeId, routeStopIdsKey]);

  useEffect(() => {
    if (!mapReady || !mapRef.current?.parentElement || !leafletMap.current) return undefined;
    const el = mapRef.current.parentElement;
    const ro = new ResizeObserver(() => {
      leafletMap.current?.invalidateSize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mapReady]);

  const drawRoutesAndMarkers = useCallback(async () => {
    if (!leafletMap.current || !mapReady) return;

    polylinesRef.current.forEach((p) => leafletMap.current.removeLayer(p));
    polylinesRef.current = [];
    markersRef.current.forEach((m) => leafletMap.current.removeLayer(m));
    markersRef.current = [];

    setRouteGeometryLoading(true);
    try {
      for (let i = 0; i < allPoints.length - 1; i++) {
        const from = allPoints[i];
        const to = allPoints[i + 1];

        try {
          const url = `${OSRM_BASE_URL}/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
          const res = await fetch(url);
          const data = await res.json();

          if (data.code === 'Ok' && data.routes?.[0]?.geometry) {
            const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
            const pl = L.polyline(coords, { color: '#a63a3a', weight: 5, opacity: 0.85 }).addTo(leafletMap.current);
            polylinesRef.current.push(pl);
          }
        } catch (err) {
          console.error(`Segment ${i + 1} failed`, err);
          const fallback = L.polyline([from, to], { color: 'red', weight: 3, dashArray: '5 10' }).addTo(leafletMap.current);
          polylinesRef.current.push(fallback);
        }

        await new Promise((r) => setTimeout(r, 80));
      }

      orderedOrders.forEach((order, idx) => {
        const lat = parseFloat(order.deliveryLatitude);
        const lng = parseFloat(order.deliveryLongitude);
        if (isNaN(lat) || isNaN(lng)) return;
        const safeAddress = escapeHtml(order.deliveryAddress || t("draft.no_address"));

        const serial = idx + 1;

        const icon = L.divIcon({
          className: 'custom-number-marker',
          html: `<div style="
          background:#a63a3a; color:white; width:34px; height:34px; border-radius:50%;
          display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:17px;
          border:2px solid white; box-shadow:0 2px 6px rgba(46, 33, 84, 0.14);
        ">${serial}</div>`,
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        });

        const marker = L.marker([lat, lng], { icon })
          .addTo(leafletMap.current)
          .bindPopup(`${escapeHtml(t("draft.stop_n", { n: serial }))}<br>${escapeHtml(t("order.title", { id: order.id }))}<br>${safeAddress}${order.clientPhone ? `<br>${phoneLinkHtml(order.clientPhone)}` : ""}`);

        markersRef.current.push(marker);
      });

      const hubIcon = L.divIcon({
        className: 'custom-number-marker',
        html: `<div style="
        background:#2f7a57; color:white; width:34px; height:34px; border-radius:50%;
        display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:18px;
        border:2px solid white; box-shadow:0 2px 6px rgba(46, 33, 84, 0.14);
      ">H</div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });

      const hubMarker = L.marker(allPoints[0], { icon: hubIcon })
        .addTo(leafletMap.current)
        .bindPopup(escapeHtml(t("draft.hub_popup")));

      markersRef.current.push(hubMarker);

      if (polylinesRef.current.length > 0) {
        const bounds = L.latLngBounds(polylinesRef.current.flatMap((p) => p.getLatLngs()));
        leafletMap.current.fitBounds(bounds, { padding: [50, 50] });
      }

      leafletMap.current.invalidateSize();
    } finally {
      setRouteGeometryLoading(false);
    }
  }, [allPoints, orderedOrders, mapReady]);

  useEffect(() => {
    if (!leafletMap.current || !mapReady) return;
    leafletMap.current.invalidateSize();
    drawRoutesAndMarkers();
  }, [allPoints, mapReady, orderedOrders, drawRoutesAndMarkers]);

  const updateDriverRoadPolyline = useCallback(async () => {
    if (!leafletMap.current || !mapReady) return;
    if (!driverPosition || !liveTargetPoint) return;

    if (liveDriverPolylineRef.current) {
      leafletMap.current.removeLayer(liveDriverPolylineRef.current);
      liveDriverPolylineRef.current = null;
    }

    const from = [driverPosition.lat, driverPosition.lng];
    const to = liveTargetPoint;
    try {
      const url = `${OSRM_BASE_URL}/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates) {
        const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        liveDriverPolylineRef.current = L.polyline(coords, {
          color: '#2f7a57',
          weight: 5,
          opacity: 0.9,
        }).addTo(leafletMap.current);
        return;
      }
    } catch (_) {
      /* fall back to straight line below */
    }

    liveDriverPolylineRef.current = L.polyline([from, to], {
      color: '#2f7a57',
      weight: 4,
      dashArray: '6 8',
      opacity: 0.85,
    }).addTo(leafletMap.current);
  }, [driverPosition, liveTargetPoint, mapReady]);

  useEffect(() => {
    if (!leafletMap.current || !mapReady) return;
    if (!driverPosition || !driverOnRoute) {
      // Route finished or driver unassigned: take the driver off the map.
      if (driverMarkerRef.current) {
        leafletMap.current.removeLayer(driverMarkerRef.current);
        driverMarkerRef.current = null;
      }
      if (liveDriverPolylineRef.current) {
        leafletMap.current.removeLayer(liveDriverPolylineRef.current);
        liveDriverPolylineRef.current = null;
      }
      return;
    }

    if (!driverMarkerRef.current) {
      const icon = L.divIcon({
        className: 'custom-driver-marker',
        html: `<div style="
          background:#2f7a57; color:white; width:34px; height:34px; border-radius:50%;
          display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px;
          border:2px solid white; box-shadow:0 2px 8px rgba(46, 33, 84, 0.14);
        ">D</div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      driverMarkerRef.current = L.marker([driverPosition.lat, driverPosition.lng], { icon })
        .addTo(leafletMap.current)
        .bindPopup(escapeHtml(t("live.driver_location")));
    } else {
      driverMarkerRef.current.setLatLng([driverPosition.lat, driverPosition.lng]);
    }
    if (followDriverRef.current) {
      leafletMap.current.panTo([driverPosition.lat, driverPosition.lng], { animate: true });
    }

    if (driverPolylineDebounceRef.current) clearTimeout(driverPolylineDebounceRef.current);
    driverPolylineDebounceRef.current = setTimeout(() => {
      updateDriverRoadPolyline();
    }, 500);
  }, [driverPosition, driverOnRoute, mapReady, updateDriverRoadPolyline]);

  useEffect(() => {
    driverMarkerRef.current?.setOpacity(driverStale ? 0.5 : 1);
  }, [driverStale, driverPosition]);

  // Panning by hand stops following the driver.
  useEffect(() => {
    const map = leafletMap.current;
    if (!map || !mapReady) return undefined;
    const stop = () => setFollowDriver(false);
    map.on('dragstart', stop);
    return () => map.off('dragstart', stop);
  }, [mapReady]);

  function toggleFollowDriver() {
    const next = !followDriver;
    setFollowDriver(next);
    if (next && driverPosition && leafletMap.current) {
      leafletMap.current.setView(
        [driverPosition.lat, driverPosition.lng],
        Math.max(leafletMap.current.getZoom(), 15),
        { animate: true }
      );
    }
  }

  function driverSeenLabel() {
    if (!driverPosition || driverSeenAt == null) return t('live.track_waiting');
    const secs = Math.max(0, Math.round((Date.now() - driverSeenAt) / 1000));
    const mins = Math.floor(secs / 60);
    if (driverStale) return t('live.track_signal_lost', { n: Math.max(1, mins) });
    if (secs < 10) return t('live.track_just_now');
    if (secs < 60) return t('live.track_seconds', { n: secs });
    return t('live.track_minutes', { n: mins });
  }

  useEffect(() => () => {
    if (driverPolylineDebounceRef.current) clearTimeout(driverPolylineDebounceRef.current);
    if (leafletMap.current && liveDriverPolylineRef.current) {
      leafletMap.current.removeLayer(liveDriverPolylineRef.current);
      liveDriverPolylineRef.current = null;
    }
    if (leafletMap.current && driverMarkerRef.current) {
      leafletMap.current.removeLayer(driverMarkerRef.current);
      driverMarkerRef.current = null;
    }
  }, []);

  const handleReBroadcast = async () => {
    if (!window.confirm(t("live.rebroadcast_confirm", { id: routeId }))) return;

    try {
      setLoading(true);
      const res = await authFetch(`/api/orders/routes/${routeId}/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      alert(t("live.rebroadcast_done"));
      const freshRes = await authFetch(`/api/orders/routes/${routeId}/details`);
      const fresh = await freshRes.json();
      setResult(fresh);
      setActionError(null);
    } catch (err) {
      setActionError(t("live.rebroadcast_failed", { reason: friendlyError(err) }));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignAndRebroadcast = async () => {
    if (!canUnassignAndRebroadcast) {
      setActionError(t("live.cannot_unassign"));
      return;
    }
    if (!window.confirm(t("live.unassign_confirm", { id: routeId }))) return;

    try {
      setLoading(true);
      const res = await authFetch(`/api/orders/routes/${routeId}/unassign-and-broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || t("live.unassign_failed_short"));
      }

      alert(t("live.unassign_done"));
      const midUnassign =
        merchantIdForMerge ||
        merchantIdFromState ||
        merchantIdFromStore ||
        result.merchantId ||
        selectedOrders[0]?.merchantId ||
        selectedOrders[0]?.merchant?.id;
      if (midUnassign != null) removeRouteDispatchMark(midUnassign, routeId);
      navigate('/broadcasted-routes');
    } catch (err) {
      setActionError(t("live.unassign_failed_reason", { reason: friendlyError(err) }));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px', color: '#1b1530', textAlign: 'center' }}>
        <h2>{t("draft.error_loading")}</h2>
        <p style={{ color: '#a63a3a' }}>{error}</p>
        <button
          onClick={() => navigate('/broadcasted-routes')}
          style={{
            padding: '10px 20px',
            background: '#e6e1f0',
            color: '#1b1530',
            border: 'none',
            borderRadius: '14px',
            marginTop: '20px',
            cursor: 'pointer',
          }}
        >
          {t("live.back_to_live")}
        </button>
      </div>
    );
  }

  return (
    <div className="route-view">
      {/* Left panel */}
      <div className="route-view__panel">
        {(() => {
          const bucket = classifyMerchantRoute({ status: result.status || effectiveStatus, pickedUp: routePickedUp });
          const live = {
            awaiting: { text: t("live.waiting_for_driver"), status: 'BROADCASTED' },
            accepted: { text: t("route.pipeline.accepted"), status: 'ASSIGNED' },
            dispatched: { text: t("route.pipeline.on_the_road"), status: 'PICKED_UP' },
            completed: { text: t("route.pipeline.completed"), status: 'COMPLETED' },
          }[bucket];
          return (
            <RouteDetailHeader
              routeId={routeId}
              eyebrow={t("live.eyebrow_route")}
              backTo="/broadcasted-routes"
              backLabel={t("live.title")}
              status={result.status || effectiveStatus}
              statusLabel={live}
              route={result}
            >
              <PipelineTrack bucket={bucket} />
            </RouteDetailHeader>
          );
        })()}
        <StepBanner flash={state?.flash} />

        {routePickedUp && (
          <div
            style={{
              marginBottom: 14,
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #b9d8c6',
              background: '#eaf4ee',
              color: '#2f7a57',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t("live.picked_up_locked")}
          </div>
        )}

        {loading && !result.orderedOrderIds?.length && (
          <div
            style={{
              marginBottom: '16px',
              padding: '14px',
              borderRadius: '10px',
              background: 'rgba(107, 78, 170, 0.25)',
              border: '1px solid #d7cdef',
              color: '#6b4eaa',
              fontWeight: 600,
            }}
          >
            {t("live.loading_details")}
          </div>
        )}


        {actionError && (
          <div style={{ background: '#a63a3a', color: '#ffffff', padding: '12px', borderRadius: '14px', margin: '16px 0', textAlign: 'center' }}>
            {actionError}
          </div>
        )}

        {result.routeId != null && (
          <DeliveryFeePanel
            routeId={parseRouteIdNumeric(result.routeId, routeId)}
            refreshKey={`${result.status}|${result.driverId}|${paymentStatusNormalized}|${result.pickedUp}`}
          />
        )}
        <DriverDebtConfirmModal
          open={!!debtConfirm}
          debtEtb={debtConfirm?.debtEtb}
          senderCashEtb={debtConfirm?.senderCashEtb}
          driverPhone={result?.driverPhone}
          onCancel={() => setDebtConfirm(null)}
          onConfirm={() => {
            setDebtConfirm(null);
            handlePayChapa();
          }}
        />

        {(effectiveStatus === 'BROADCASTED' || effectiveStatus === 'ASSIGNED') && (
          <div
            style={{
              marginTop: '24px',
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid #d7cdef',
              background:
                'linear-gradient(145deg, #f3effb, #f3effb)',
            }}
          >
            <h3 style={{ color: '#1b1530', marginTop: 0, marginBottom: '8px' }}>{t("price.title")}</h3>
            <p style={{ color: '#6a6480', fontSize: '0.9rem', marginTop: 0, marginBottom: '14px' }}>
              {t("live.pricing_hint")}
            </p>

            {costPreviewLoading && (
              <p style={{ color: '#6b4eaa', marginBottom: '12px' }}>{t("price.loading_breakdown")}</p>
            )}
            {costPreviewError && (
              <p style={{ color: '#a63a3a', fontSize: '0.9rem', marginBottom: '12px' }}>{costPreviewError}</p>
            )}

            {costPreview && costPreviewDerived && !costPreviewLoading && (
              <>
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid #d7cdef',
                    background: 'rgba(46, 33, 84, 0.14)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', color: '#8467c2', fontWeight: 600 }}>
                        {t("price.direct_to_driver")}
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 800, color: '#6b4eaa', marginTop: '4px' }}>
                        {t("common.amount_etb", { amount: costPreviewDerived.dir.toFixed(2) })}
                      </div>
                      {!isSingleStopRoute && costPreviewDerived.bonus > 0.005 && (
                        <div
                          style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#2f7a57',
                          }}
                        >
                          {t("price.save", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-expanded={costBreakdownExpanded}
                      onClick={() => setCostBreakdownExpanded(!costBreakdownExpanded)}
                      style={{
                        flexShrink: 0,
                        padding: '8px 12px',
                        borderRadius: '10px',
                        border: '1px solid #d7cdef',
                        background: '#f3effb',
                        color: '#6b4eaa',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '13px',
                      }}
                    >
                      {costBreakdownExpanded ? t("price.hide_detail") : t("price.show_detail")}
                    </button>
                  </div>
                </div>

                {costBreakdownExpanded && (
                  <div style={{ marginTop: '12px' }}>
                    {!isSingleStopRoute && (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                          {costPreview.vehicleType ? (
                            <span
                              style={{
                                padding: '6px 10px',
                                borderRadius: '20px',
                                background: '#f1eef8',
                                color: '#1b1530',
                                fontSize: '12px',
                                fontWeight: 700,
                              }}
                            >
                              {t("price.vehicle", { v: formatVehicleLabel(costPreview.vehicleType) })}
                            </span>
                          ) : null}
                          <span
                            style={{
                              padding: '6px 10px',
                              borderRadius: '20px',
                              background: '#f1eef8',
                              color: '#1b1530',
                              fontSize: '12px',
                              fontWeight: 700,
                            }}
                          >
                            {t("price.rate", { rate: costPreviewDerived.vehiclePricePerKm.toFixed(2) })}
                          </span>
                        </div>

                        {costPreviewDerived.bonus > 0.005 && (
                          <div
                            style={{
                              padding: '14px',
                              borderRadius: '12px',
                              border: '1px solid #b9d8c6',
                              background:
                                'linear-gradient(125deg, #eaf4ee, #eaf4ee)',
                              marginBottom: '10px',
                            }}
                          >
                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#2f7a57' }}>{t("price.savings")}
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: 900, color: '#2f7a57', marginTop: '6px' }}>
                              −{t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) })}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div
                      style={{
                        padding: '12px',
                        borderRadius: '12px',
                        border: '1px solid #e6e1f0',
                        background: '#fcfbfe',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#6b4eaa', marginBottom: '10px' }}>
                        {t("price.payment_breakdown")}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.direct_to_driver_short")}</span>
                        <strong style={{ color: '#1b1530' }}>{t("common.amount_etb", { amount: costPreviewDerived.dir.toFixed(2) })}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.service_fee")}</span>
                        <strong style={{ color: '#1b1530' }}>{t("common.amount_etb", { amount: costPreviewDerived.svc.toFixed(2) })}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.total_you_pay")}</span>
                        <strong style={{ color: '#6b4eaa' }}>{t("common.amount_etb", { amount: costPreviewDerived.totalYouPay.toFixed(2) })}</strong>
                      </div>
                    </div>

                    <div
                      style={{
                        padding: '12px',
                        borderRadius: '12px',
                        border: '1px solid #e6e1f0',
                        background: '#fcfbfe',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#6b4eaa', marginBottom: '10px' }}>
                        {t("price.per_stop")}
                      </div>
                      {orderedStopIds.map((orderId, i) => {
                        const order = selectedOrders.find((o) => o.id === orderId);
                        const address =
                          order?.deliveryAddress && String(order.deliveryAddress).trim() !== ''
                            ? order.deliveryAddress
                            : t("order.title", { id: orderId });
                        const alone = costPreviewDerived.alone?.[orderId];
                        const batchVal = actualStopCostFromPreview(
                          costPreview,
                          costPreviewDerived.alone,
                          costPreviewDerived.actual,
                          orderId
                        );
                        const saved =
                          alone != null && !isSingleStopRoute
                            ? Math.max(0, alone - batchVal)
                            : null;
                        return (
                          <div
                            key={orderId}
                            style={{
                              marginBottom: '10px',
                              padding: '10px',
                              borderRadius: '10px',
                              border: '1px solid #ede9f6',
                              background: '#ffffff',
                            }}
                          >
                            <div style={{ fontSize: '13px', color: '#1b1530', fontWeight: 600 }}>
                              {t("price.stop_address", { n: i + 1, address })}
                            </div>
                            {order?.clientPhone && String(order.clientPhone).trim() !== '' && (
                              <div style={{ marginTop: '6px', fontSize: '12px' }}>
                                <a
                                  href={`tel:${String(order.clientPhone).replace(/\s/g, '')}`}
                                  style={{ color: '#2f7a57', textDecoration: 'none', fontWeight: 700 }}
                                >
                                  {order.clientPhone}
                                </a>
                              </div>
                            )}
                            {!isSingleStopRoute && alone != null ? (
                              <div
                                style={{
                                  marginTop: '8px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  textAlign: 'right',
                                  color: '#6b4eaa',
                                  lineHeight: 1.45,
                                }}
                              >
                                <span style={{ color: '#a63a3a' }}>{t("price.alone", { amount: t("common.amount_etb", { amount: alone.toFixed(2) }) })}</span>
                                <br />
                                <span style={{ color: '#2f7a57' }}>{t("price.batch", { amount: t("common.amount_etb", { amount: batchVal.toFixed(2) }) })}</span>
                                {saved != null && saved > 0.005 ? (
                                  <>
                                    <br />
                                    <span style={{ color: '#2f7a57' }}>{t("price.saved", { amount: t("common.amount_etb", { amount: saved.toFixed(2) }) })}</span>
                                  </>
                                ) : null}
                              </div>
                            ) : (
                              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6a6480' }}>-</div>
                            )}
                          </div>
                        );
                      })}
                      {!isSingleStopRoute && (
                        <>
                          <hr style={{ borderColor: '#e6e1f0', margin: '12px 0' }} />
                          <div style={{ fontSize: '13px', color: '#6a6480', marginBottom: '6px' }}>
                            {t("price.total_alone")}{' '}
                            <strong style={{ color: '#a63a3a' }}>
                              {t("common.amount_etb", { amount: costPreviewDerived.totalAlone.toFixed(2) })}
                            </strong>
                          </div>
                          <div style={{ fontSize: '13px', color: '#2f7a57', fontWeight: 700 }}>
                            {t("price.you_save", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                          </div>
                          <div style={{ marginTop: '10px', fontSize: '13px', color: '#6a6480' }}>
                            {t("price.route_earnings", { amount: t("common.amount_etb", { amount: Number(costPreview.baseEarning ?? 0).toFixed(2) }) })}
                            {costPreviewDerived.bonus > 0.005 ? (
                              <span style={{ color: '#2f7a57', fontWeight: 800 }}>
                                {' '}
                                {t("price.bonus", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                              </span>
                            ) : (
                              <span style={{ color: '#6a6480' }}> {t("price.no_bonus")}</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {!paymentCompleted && !isAssigned && (
              <p style={{ marginTop: '18px', fontSize: '13px', color: '#6a6480' }}>
                {t("live.pay_after_accept")}
              </p>
            )}
            {!paymentCompleted && isAssigned && Number(result?.driverDebtDeductionEtb || 0) > 0 && (
              <p style={{ marginTop: '18px', fontSize: '13px', color: '#1e3a8a' }}>
                {t("live.debt_included", { debt: Number(result.driverDebtDeductionEtb).toFixed(2) })}
              </p>
            )}
            {!paymentCompleted && isAssigned && (
              <div style={{ marginTop: '18px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button
                  type="button"
                  onClick={startPayment}
                  disabled={paymentBusy}
                  style={{
                    padding: '12px 18px',
                    background: paymentBusy ? '#a59eae' : '#6b4eaa',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '14px',
                    fontWeight: 600,
                    cursor: paymentBusy ? 'wait' : 'pointer',
                  }}
                >
                  {paymentBusy ? t("live.starting") : t("live.pay")}
                </button>
              </div>
            )}
            {paymentCompleted && (
              <p style={{ color: '#2f7a57', marginTop: '16px', fontWeight: 600 }}>
                {t("live.payment_completed")}
              </p>
            )}
          </div>

        )}

        <h3 style={{ color: '#1b1530' }}>{t("draft.sequence", { n: orderedOrders.length })}</h3>

        <ol style={{ paddingLeft: '0', margin: '0' }}>
          {orderedOrders.map((order, idx) => (
            <li
              key={order.id}
              style={{
                margin: '12px 0',
                padding: '12px',
                background: '#ffffff',
                borderRadius: '14px',
                border: '1px solid #e6e1f0',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#a63a3a',
                  color: '#ffffff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginRight: '12px',
                  border: '2px solid white',
                }}
              >
                {idx + 1}
              </div>
              <div style={{ flex: 1 }}>
                <strong>{t("order.title", { id: order.id })}</strong><br />
                <span style={{ color: '#6a6480' }}>
                  {order.deliveryAddress || t("draft.no_address")}
                </span><br />
                <small style={{ color: '#6a6480' }}>
                  {t("draft.distance_from_previous", { d: result.consecutiveDistances?.[idx] || "-" })}
                </small>
                {order.clientPhone && String(order.clientPhone).trim() !== '' && (
                  <div style={{ marginTop: '6px', fontSize: '12px' }}>
                    <span style={{ color: '#6b4eaa', fontWeight: 600 }}>Phone: </span>
                    <a
                      href={`tel:${String(order.clientPhone).replace(/\s/g, '')}`}
                      style={{ color: '#2f7a57', textDecoration: 'none' }}
                    >
                      {order.clientPhone}
                    </a>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>

        <p style={{ marginTop: '16px', color: '#1b1530' }}>
          {result.tourMode !== 'ONE_WAY' ? (
            <>
              <strong>{t("live.return_to_hub_distance")}</strong> {result.consecutiveDistances?.[orderedOrders.length] || "-"}
            </>
          ) : (
            <span style={{ color: '#6a6480' }}>{t("draft.one_way_note")}</span>
          )}
        </p>

        {/* Simplified QR Code - with white background */}
        <div style={{ marginTop: '32px' }}>
          <h3 style={{ color: '#1b1530' }}>{t("live.qr_title")}</h3>
          <div
            style={{
              textAlign: 'center',
              margin: '16px 0',
              padding: '20px',
              backgroundColor: '#ffffff', // White background
              borderRadius: '12px',
              display: 'inline-block',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {qrPayloadForDisplay ? (
              <>
                <div style={{
                  backgroundColor: '#ffffff',
                  padding: '10px',
                  display: 'inline-block',
                  borderRadius: '14px'
                }}>
                  <QRCode
                    value={qrPayloadForDisplay}
                    size={200}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#1b1530"
                  />
                </div>
              </>
            ) : (
              <p style={{ color: '#6a6480' }}>
                {paymentCompleted
                  ? t("live.qr_not_ready")
                  : t("live.qr_locked")}
              </p>
            )}
          </div>
          <p style={{ color: '#6a6480', fontSize: '0.9rem', textAlign: 'center' }}>
            {t("live.qr_hint")}
          </p>
        </div>

        {/* Driver / broadcast status (actions live in the pinned bar below) */}
        {effectiveStatus === 'ASSIGNED' ? (
          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#1b1530' }}>{t("live.driver")}</h3>
            <div
              style={{
                padding: '16px',
                background: '#eaf4ee',
                border: '1px solid #b9d8c6',
                borderRadius: '14px',
              }}
            >
              <strong>{t("live.assigned_to", { id: result.driverId })}</strong>
              <br />
              <small>
                {t("readymap.phone")}{' '}
                {result.driverPhone ? (
                  <a href={`tel:+${String(result.driverPhone).replace(/^\+/, '')}`} style={{ color: '#6b4eaa', fontWeight: 600 }}>
                    +{String(result.driverPhone).replace(/^\+/, '')}
                  </a>
                ) : (
                  '-'
                )}
              </small>
            </div>
          </div>
        ) : (
          <p style={{ color: '#6a6480', fontSize: '0.9rem', marginTop: '20px', textAlign: 'center' }}>
            {t("live.nearby_can_claim")}
          </p>
        )}

        <div className="route-view__actions">
          {effectiveStatus === 'ASSIGNED' ? (
            canUnassignAndRebroadcast ? (
              <button type="button" className="danger" onClick={handleUnassignAndRebroadcast} disabled={loading}>
                {t("live.unassign")}
              </button>
            ) : null
          ) : (
            <button type="button" className="primary" onClick={handleReBroadcast} disabled={loading}>
              {loading ? t("live.rebroadcasting") : t("live.rebroadcast")}
            </button>
          )}
          <button type="button" onClick={() => navigate('/broadcasted-routes')} disabled={loading}>
            {t("common.back")}
          </button>
        </div>
      </div>

      {/* Map */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          background: '#f1eef8',
          boxShadow: '0 8px 30px rgba(46, 33, 84, 0.14)',
        }}
      >
        <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

        {driverOnRoute && mapReady ? (
          <div className="live-track-chip" role="status" aria-live="polite">
            <span
              className={`live-track-chip__dot${!driverPosition || driverStale ? ' is-warn' : ''}`}
              aria-hidden="true"
            />
            <span className="live-track-chip__text">{driverSeenLabel()}</span>
            {driverPosition ? (
              <button
                type="button"
                className={`live-track-chip__follow${followDriver ? ' is-on' : ''}`}
                aria-pressed={followDriver}
                onClick={toggleFollowDriver}
              >
                <i className="fa-solid fa-location-crosshairs" aria-hidden="true" />{' '}
                {followDriver ? t('live.track_following') : t('live.track_follow')}
              </button>
            ) : null}
          </div>
        ) : null}

        {(loading || routeGeometryLoading) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(46, 33, 84, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b1530',
              fontSize: '1.4rem',
              zIndex: 10,
            }}
          >
            {routeGeometryLoading
              ? t("live.updating_map")
              : loading
                ? t("common.loading")
                : ''}
          </div>
        )}
      </div>
    </div>
  );
}